/**
 * Безопасные обёртки над localStorage/sessionStorage.
 *
 * В режиме инкогнито, при строгом CSP или исчерпанной квоте
 * вызов Storage API бросает исключение → краш React-дерева → белый экран.
 *
 * Решение: try-catch на каждый вызов.
 * При ошибке getItem → null, setItem/removeItem → no-op.
 */

interface SafeStorage {
  getItem: (name: string) => string | null;
  setItem: (name: string, value: string) => void;
  removeItem: (name: string) => void;
}

function createSafeStorage(getStorage: () => Storage): SafeStorage {
  return {
    getItem(name: string): string | null {
      try {
        return getStorage().getItem(name);
      } catch {
        return null;
      }
    },
    setItem(name: string, value: string): void {
      try {
        getStorage().setItem(name, value);
      } catch {
        // Квота исчерпана или хранилище недоступно
      }
    },
    removeItem(name: string): void {
      try {
        getStorage().removeItem(name);
      } catch {
        // Хранилище недоступно
      }
    },
  };
}

/** Безопасная обёртка над localStorage для Zustand persist */
export const safeLocalStorage: SafeStorage = createSafeStorage(() => localStorage);

/** Безопасная обёртка над sessionStorage */
export const safeSessionStorage: SafeStorage = createSafeStorage(() => sessionStorage);

/** Безопасное чтение. Возвращает null при ошибке. */
export function safeGetItem(key: string, storage: 'local' | 'session' = 'local'): string | null {
  try {
    return storage === 'local'
      ? localStorage.getItem(key)
      : sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Безопасная запись. No-op при ошибке. */
export function safeSetItem(key: string, value: string, storage: 'local' | 'session' = 'local'): void {
  try {
    if (storage === 'local') {
      localStorage.setItem(key, value);
    } else {
      sessionStorage.setItem(key, value);
    }
  } catch {
    // Квота исчерпана или хранилище недоступно
  }
}

/** Безопасное удаление. No-op при ошибке. */
export function safeRemoveItem(key: string, storage: 'local' | 'session' = 'local'): void {
  try {
    if (storage === 'local') {
      localStorage.removeItem(key);
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    // Хранилище недоступно
  }
}
